import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { LoadingButton } from "@rentablo/components";
import {
  AccountHolder,
  SynchronizationBatchJob,
  useBankConnectionsCashbackV2,
  useUpdateAccountHolder,
} from "@rentablo/api/apiv1";
import { SendEventType } from "../stateMachine/typings/SendEventType";
import { AccountHolderSelector } from "./AccountHolderSelector";

const isEnabled = (accountHolder: AccountHolder) => {
  return (
    accountHolder.isSyncEnabled ||
    accountHolder.isSyncConsentRequired ||
    !accountHolder.numberOfActiveOrTerminatedSubscriptions
  );
};

export interface Props {
  sendEvent: SendEventType;
  accountHolders: AccountHolder[];
}

export const AccountHolderSettings = ({ sendEvent, accountHolders }: Props) => {
  const { t } = useTranslation();
  const [checked, setChecked] = useState<number[]>(() =>
    accountHolders.reduce((list, accountHolder) => {
      if (isEnabled(accountHolder)) list.push(accountHolder.id);
      return list;
    }, [] as number[])
  );

  const { mutateAsync: updateAccountHolder, isPending } =
    useUpdateAccountHolder({ throwOnError: true });

  const { mutate: importCashback, isPending: isImportLoading } =
    useBankConnectionsCashbackV2({
      throwOnError: true,
      onSuccess: (batchJob: SynchronizationBatchJob) => {
        sendEvent({
          type: "IMPORT_DATA",
          depots: [],
          externalDepotIds: batchJob.externalDepotIds,
        });
      },
    });

  const toggleChecked = (id: number, isSyncEnabled: boolean) => {
    isSyncEnabled
      ? setChecked((current) => [...current, id])
      : setChecked((current) => current.filter((rec) => rec !== id));
  };

  const handleChange = async (id: number) => {
    const isSyncEnabled = checked.includes(id);
    const data = await updateAccountHolder({
      id: id,
      createOrUpdateAccountHolderParams: {
        isSyncEnabled: !isSyncEnabled,
      },
    });
    toggleChecked(data.id, data.isSyncEnabled);
  };

  return (
    <>
      <Typography variant="h4" component="h1" mb={6}>
        {t("netfondsConsent.selectAccountHolders")}
      </Typography>

      <Typography variant="body1" mb={6}>
        {t("netfondsConsent.selectAccountHoldersDescription")}
      </Typography>

      <Box mb={6}>
        {accountHolders.map((accountHolder) => {
          return (
            <AccountHolderSelector
              id={accountHolder.id}
              key={accountHolder.id}
              isChecked={!!checked.find((rec) => rec === accountHolder.id)}
              firstName={accountHolder.naturalPersons?.[0]?.firstName ?? ""}
              lastName={accountHolder.naturalPersons?.[0]?.lastName ?? ""}
              handleChange={handleChange}
            />
          );
        })}
      </Box>
      <Box display="flex" gap={2}>
        <Button
          data-testid="back-button"
          variant="tertiary"
          disabled={isImportLoading || isPending}
          onClick={() => sendEvent({ type: "BACK" })}
        >
          {t("navigation.previous")}
        </Button>
        <LoadingButton
          data-testid="submit-importCashback"
          variant="primary"
          isFetching={isImportLoading || isPending}
          onClick={() => importCashback()}
        >
          {t("netfondsConsent.activateDataImport")}
        </LoadingButton>
      </Box>
    </>
  );
};
